import React, { useEffect, useState } from 'react';
import { LpBuilderFormMode, LpBuilderSidebarItems } from '../../constants';
import { SaveUIKitAction, UIKit } from '../uikit/UIKit';
import {
  IUIKitsQueryParams,
  IUIKitsResponse,
} from 'lib/api/superadmin/uikits/useQueryUIKits';
import { ImageForm } from './ImageForm';
import { CTAForm } from './CTAForm';
import { BodyForm } from './BodyForm';
import { FooterForm } from './FooterForm';
import { HeaderForm } from './HeaderForm';
import { SocialIconsForm } from './SocialIconsForm';
import { FetchNextPageOptions, InfiniteQueryObserverResult } from 'react-query';
import { FontFamilyOption } from 'app/pages/design/landingPageBuilder/components';
import { useFormikContext } from 'formik';
import { ILandingPageBuilderForm, IUIKitFormData } from '../../types';

type LpBuilderFormProps = {
  params: IUIKitsQueryParams;
  setShowLandingPagePreview: (isCreating: boolean) => void;
  activeForm?: LpBuilderSidebarItems;
  saveAction: SaveUIKitAction | null;
  searchUIKitsQuery: string;
  setSearchUIKitsQuery: React.Dispatch<React.SetStateAction<string>>;
  setSaveAction: React.Dispatch<React.SetStateAction<SaveUIKitAction | null>>;
  fetchNextPage: (
    options?: FetchNextPageOptions
  ) => Promise<InfiniteQueryObserverResult<IUIKitsResponse, unknown>>;
  totalCount: number;
  pageTotal: number;
  isLoadingUIKits: boolean;
  allFontFamilyOptions: FontFamilyOption[];
  setIsUIKitCreate: React.Dispatch<React.SetStateAction<boolean>>;
  setIsUIKitUpdate: React.Dispatch<React.SetStateAction<boolean>>;
  isUIKitCreate: boolean;
  isUIKitUpdate: boolean;
  scrollToTop: () => void;
  mode: LpBuilderFormMode;
};

export const LandingPageBuilderForm = ({
  params,
  setShowLandingPagePreview,
  activeForm,
  saveAction,
  searchUIKitsQuery,
  setSearchUIKitsQuery,
  setSaveAction,
  fetchNextPage,
  totalCount,
  pageTotal,
  isLoadingUIKits,
  allFontFamilyOptions,
  setIsUIKitCreate,
  isUIKitCreate,
  setIsUIKitUpdate,
  isUIKitUpdate,
  scrollToTop,
  mode,
}: LpBuilderFormProps) => {
  const { values, setFieldValue } = useFormikContext<ILandingPageBuilderForm>();
  const [selectedUIKitIndex, setSelectedUIKitIndex] = useState<number | null>(
    null
  );

  const [tempState, setTempState] = useState<IUIKitFormData | null>(null);

  const handleRemove = () => {
    const currentUiKits = values.uiKits;
    if (currentUiKits.length > 0) {
      const newUiKits = [...currentUiKits];
      newUiKits.splice(currentUiKits.length - 1, 1);
      setFieldValue('uiKits', newUiKits);
    }
  };

  const handleUIKitGoBack = () => {
    if (isUIKitCreate) {
      handleRemove();
      setIsUIKitCreate(false);
    }
    if (isUIKitUpdate) {
      setFieldValue(`uiKits[${selectedUIKitIndex}]`, tempState);
      setTempState(null);
      setIsUIKitUpdate(false);
    }
    setSelectedUIKitIndex(null);
    setShowLandingPagePreview(true);
    setSaveAction(null);
  };

  useEffect(() => {
    if (tempState === null && selectedUIKitIndex !== null && isUIKitUpdate) {
      const selectedUIKit = values.uiKits[selectedUIKitIndex];
      setTempState(selectedUIKit);
    }
    scrollToTop();
  }, [selectedUIKitIndex]);

  useEffect(() => {
    handleUIKitGoBack();
  }, [activeForm]);

  const formMapping = {
    [LpBuilderSidebarItems.UI_KIT]: (
      <UIKit
        setShowLandingPagePreview={setShowLandingPagePreview}
        params={params}
        setSearchQuery={setSearchUIKitsQuery}
        searchQuery={searchUIKitsQuery}
        setSaveAction={setSaveAction}
        saveAction={saveAction}
        fetchNextPage={fetchNextPage}
        totalCount={totalCount}
        pageTotal={pageTotal}
        isLoadingUIKits={isLoadingUIKits}
        allFontFamilyOptions={allFontFamilyOptions}
        selectedUIKitIndex={selectedUIKitIndex}
        handleGoBack={handleUIKitGoBack}
        setSelectedUIKitIndex={setSelectedUIKitIndex}
        setIsCreate={setIsUIKitCreate}
        setIsUpdate={setIsUIKitUpdate}
        scrollToTop={scrollToTop}
      />
    ),
    [LpBuilderSidebarItems.IMAGE]: <ImageForm mode={mode} />,
    [LpBuilderSidebarItems.CTA]: (
      <CTAForm allFontFamilyOptions={allFontFamilyOptions} mode={mode} />
    ),
    [LpBuilderSidebarItems.BODY]: (
      <BodyForm allFontFamilyOptions={allFontFamilyOptions} mode={mode} />
    ),
    [LpBuilderSidebarItems.HEADER]: (
      <HeaderForm allFontFamilyOptions={allFontFamilyOptions} mode={mode} />
    ),
    [LpBuilderSidebarItems.FOOTER]: <FooterForm mode={mode} />,
    [LpBuilderSidebarItems.SOCIAL_MEDIA]: <SocialIconsForm mode={mode} />,
  };
  const getActiveForm = () => {
    return activeForm ? formMapping[activeForm] : null;
  };

  return getActiveForm();
};
